import React, { useState, useEffect } from 'react';
import { api } from '../../config/api';

const GenerateWordsModal = ({ isOpen, onClose, onSave, listId, language }) => {
  const [topic, setTopic] = useState('');
  const [wordCount, setWordCount] = useState(20);
  const [generatedWords, setGeneratedWords] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [regenerationCount, setRegenerationCount] = useState(0);
  const [selectedWords, setSelectedWords] = useState([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [rejectedWords, setRejectedWords] = useState([]);

  // Funkcja pomocnicza do usuwania duplikatów
  const removeDuplicates = (words) => {
    const seen = new Set();
    return words.filter(word => {
      const duplicate = seen.has(word.word);
      seen.add(word.word);
      return !duplicate;
    });
  };

  // Resetowanie stanu po zamknięciu modalu
  useEffect(() => {
    if (!isOpen) {
      setTopic('');
      setWordCount(5);
      setGeneratedWords([]);
      setError('');
      setRegenerationCount(0);
      setSelectedWords([]);
      setIsGenerating(false);
      setRejectedWords([]);
    }
  }, [isOpen]);

  const handleClose = () => {
    onClose();
  };

  const handleGenerate = async () => {
    if (!topic.trim()) {
      setError('Proszę podać temat');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const response = await api.generateWords({
        jezyk: language,
        temat: topic,
        liczba_slowek: wordCount,
        existing_words: removeDuplicates([...generatedWords, ...rejectedWords])
      });

      const newWords = response.response.words;
      setGeneratedWords(prev => removeDuplicates([...prev, ...newWords]));
      setSelectedWords(prev => removeDuplicates([...prev, ...newWords]));
      setIsGenerating(true);
    } catch (err) {
      setError(err.message || 'Wystąpił błąd podczas generowania słówek');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRegenerate = async () => {
    if (regenerationCount >= 5) {
      setError('Osiągnięto maksymalną liczbę regeneracji (5)');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const response = await api.generateWords({
        jezyk: language,
        temat: topic,
        liczba_slowek: wordCount,
        existing_words: removeDuplicates([...generatedWords, ...rejectedWords])
      });

      const newWords = response.response.words;
      setGeneratedWords(prev => removeDuplicates([...prev, ...newWords]));
      setSelectedWords(prev => removeDuplicates([...prev, ...newWords]));
      setRegenerationCount(prev => prev + 1);
    } catch (err) {
      setError(err.message || 'Wystąpił błąd podczas regeneracji słówek');
    } finally {
      setIsLoading(false);
    }
  };

  const handleReplaceUnselected = async () => {
    const unselectedWords = generatedWords.filter(word => 
      !selectedWords.some(w => w.word === word.word)
    );

    if (unselectedWords.length === 0) {
      setError('Nie ma odznaczonych słówek do zastąpienia');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const response = await api.generateWords({
        jezyk: language,
        temat: topic,
        liczba_slowek: unselectedWords.length,
        existing_words: removeDuplicates([...generatedWords, ...rejectedWords])
      });

      const newWords = response.response.words;
      setGeneratedWords(prev => 
        removeDuplicates([
          ...prev.filter(word => selectedWords.some(w => w.word === word.word)),
          ...newWords
        ])
      );
      setSelectedWords(prev => removeDuplicates([...prev, ...newWords]));
      setRegenerationCount(prev => prev + 1);
    } catch (err) {
      setError(err.message || 'Wystąpił błąd podczas zastępowania słówek');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      await onSave(removeDuplicates(selectedWords));
      handleClose();
    } catch (err) {
      setError(err.message || 'Wystąpił błąd podczas zapisywania słówek');
    }
  };

  const toggleWordSelection = (word) => {
    setSelectedWords(prev => {
      const isSelected = prev.some(w => w.word === word.word);
      if (isSelected) {
        // Dodaj do odrzuconych słówek
        setRejectedWords(prev => removeDuplicates([...prev, word]));
        return prev.filter(w => w.word !== word.word);
      } else {
        // Usuń z odrzuconych słówek
        setRejectedWords(prev => prev.filter(w => w.word !== word.word));
        return removeDuplicates([...prev, word]);
      }
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-xl relative">
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 text-neutral-400 hover:text-neutral-600 transition-colors"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        <div className="p-4">
          <h2 className="text-sm font-semibold mb-4">Generuj słówka</h2>
          
          {!isGenerating ? (
            <>
              <div className="mb-3">
                <label className="block text-xs font-medium text-neutral-700 mb-1">
                  Temat
                </label>
                <input
                  type="text"
                  value={topic}
                  onChange={(e) => setTopic(e.target.value.slice(0, 100))}
                  className="w-full px-3 py-2 text-xs border border-neutral-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary-500"
                  placeholder="np. podróże, jedzenie, praca"
                  maxLength="100"
                />
              </div>

              <div className="mb-3">
                <label className="block text-xs font-medium text-neutral-700 mb-1">
                  Liczba słówek (max 20)
                </label>
                <input
                  type="number"
                  value={wordCount}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === '') {
                      setWordCount('');
                    } else {
                      const numValue = parseInt(value);
                      if (!isNaN(numValue)) {
                        setWordCount(Math.min(10, Math.max(1, numValue)));
                      }
                    }
                  }}
                  onBlur={(e) => {
                    if (e.target.value === '') {
                      setWordCount(1);
                    }
                  }}
                  className="w-full px-3 py-2 text-xs border border-neutral-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary-500"
                  min="1"
                  max="20"
                />
              </div>

              {error && (
                <div className="mb-4 p-3 text-sm bg-red-50 text-red-600 rounded-md">
                  {error}
                </div>
              )}

              <div className="flex justify-end gap-2">
                <button
                  onClick={handleClose}
                  className="px-3 py-1.5 text-xs font-medium text-neutral-700 bg-white border border-neutral-300 rounded-md hover:bg-neutral-50 transition-colors"
                >
                  Anuluj
                </button>
                <button
                  onClick={handleGenerate}
                  disabled={isLoading || !topic.trim()}
                  className="px-3 py-1.5 text-xs font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? 'Generowanie...' : 'Generuj'}
                </button>
              </div>
            </>
          ) : (
            <>
              {error && (
                <div className="mb-4 p-3 text-sm bg-red-50 text-red-600 rounded-md">
                  {error}
                </div>
              )}

              <div className="mb-4">
                <h3 className="text-sm font-medium mb-2">Wygenerowane słówka</h3>
                <div className="grid grid-cols-2 gap-2">
                  {generatedWords.map((word, index) => (
                    <div
                      key={index}
                      className={`p-2 border rounded-md cursor-pointer ${
                        selectedWords.some(w => w.word === word.word)
                          ? 'border-primary-600 bg-primary-50'
                          : 'border-neutral-300'
                      }`}
                      onClick={() => toggleWordSelection(word)}
                    >
                      <div className="text-sm font-medium">{word.word}</div>
                      <div className="text-xs text-neutral-600">{word.translation}</div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex justify-between gap-2">
                <div className="flex gap-2">
                  <button
                    onClick={handleClose}
                    className="px-3 py-1.5 text-xs font-medium text-neutral-700 bg-white border border-neutral-300 rounded-md hover:bg-neutral-50 transition-colors"
                  >
                    Anuluj
                  </button>
                  <button
                    onClick={handleSave}
                    disabled={selectedWords.length === 0}
                    className="px-3 py-1.5 text-xs font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Zapisz wybrane ({selectedWords.length})
                  </button>
                </div>
                <button
                  onClick={handleReplaceUnselected}
                  disabled={isLoading || regenerationCount >= 5}
                  className="px-3 py-1.5 text-xs font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Zastąp odznaczone
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default GenerateWordsModal; 